import { useState, useEffect } from 'react'
import { apiClient } from '../services/api'
import { useTranslation } from '../contexts/TranslationContext'
import { useAuthStore } from '../services/authStore'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import { Plus, Edit3, Trash2, Save, X, ChevronUp, ChevronDown } from 'lucide-react'
import { ui } from '../styles/ui'
import { cn } from '../utils/cn'
import { formatEmptyValue } from '../utils/displayValue'

export default function Categories({ categoryType = 'product', pageTitle = '' }) {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedIds, setSelectedIds] = useState([])
  const [bulkAction, setBulkAction] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmMessage, setConfirmMessage] = useState('')
  const [confirmAction, setConfirmAction] = useState(null)
  const [groupFilter, setGroupFilter] = useState('')
  const { t, locale } = useTranslation()
  const [searchText, setSearchText] = useState('')
  const [bulkGroup, setBulkGroup] = useState('')
  const [bulkColor, setBulkColor] = useState('#4ECDC4')
  const [showImportDefaults, setShowImportDefaults] = useState(false)
  const [form, setForm] = useState({ name: '', color: '#4ECDC4', icon: '', description: '', order: 0, group: '', parent_id: null })
  const [editingId, setEditingId] = useState(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const { user } = useAuthStore()
  const isAdmin = Boolean(user?.is_admin ?? user?.isAdmin)
  const isReceipt = categoryType === 'receipt'
  const title = pageTitle || (isReceipt ? t('categories.receipt_title', 'Receipt Categories') : t('categories.product_title', 'Product Categories'))

  const translateDefaultCategoryName = (name = '') => {
    const normalized = String(name).trim().toLowerCase()
    if (isReceipt) {
      switch (normalized) {
        case 'food': return t('categories.default.receipt.food', 'Food')
        case 'home': return t('categories.default.receipt.home', 'Home')
        case 'transport': return t('categories.default.receipt.transport', 'Transport')
        case 'health': return t('categories.default.receipt.health', 'Health')
        case 'shopping': return t('categories.default.receipt.shopping', 'Shopping')
        case 'utilities': return t('categories.default.receipt.utilities', 'Utilities')
        case 'entertainment': return t('categories.default.receipt.entertainment', 'Entertainment')
        case 'travel': return t('categories.default.receipt.travel', 'Travel')
        case 'other': return t('categories.default.receipt.other', 'Other')
        default: return name
      }
    }

    switch (normalized) {
      case 'groceries': return t('categories.default.product.groceries', 'Groceries')
      case 'drinks': return t('categories.default.product.drinks', 'Drinks')
      case 'household': return t('categories.default.product.household', 'Household')
      case 'pharmacy': return t('categories.default.product.pharmacy', 'Pharmacy')
      case 'electronics': return t('categories.default.product.electronics', 'Electronics')
      case 'transport': return t('categories.default.product.transport', 'Transport')
      case 'clothing': return t('categories.default.product.clothing', 'Clothing')
      case 'services': return t('categories.default.product.services', 'Services')
      case 'other': return t('categories.default.product.other', 'Other')
      default: return name
    }
  }

  const normalizeCategory = (category) => ({
    ...category,
    parent_id: category.parentId ?? category.parent_id ?? null,
    is_default: category.isDefault ?? category.is_default ?? false,
    localized_name: category.localized_name ?? translateDefaultCategoryName(category.name ?? ''),
    localized_description: category.localized_description ?? category.description ?? '',
  })

  useEffect(() => {
    fetchCategories()
  }, [locale, categoryType])

  const fetchCategories = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get('/categories', { params: { type: categoryType, locale } })
      const payload = response.data?.data ?? response.data
      const normalized = Array.isArray(payload) ? payload.map(normalizeCategory) : []
      setCategories(normalized)
      setShowImportDefaults(normalized.length === 0)
      setSelectedIds([])
      setCurrentPage(1)
    } catch (err) {
      setError(t('categories.failed_load', 'Failed to load categories'))
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setForm({ name: '', color: '#4ECDC4', icon: '', description: '', order: 0, group: '', parent_id: null })
    setEditingId(null)
    setError('')
  }

  const closeEditModal = () => {
    setEditModalOpen(false)
    resetForm()
  }

  const openCreateModal = () => {
    resetForm()
    setEditModalOpen(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError(t('categories.name_required', 'Name is required'))
      return
    }

    try {
      setError('')
      const payload = { ...form, category_type: categoryType }
      if (editingId) {
        await apiClient.put(`/categories/${editingId}`, payload)
      } else {
        await apiClient.post('/categories', payload)
      }
      fetchCategories()
      closeEditModal()
    } catch (err) {
      setError(err.response?.data?.message || t('categories.failed_save', 'Failed to save category'))
    }
  }

  const handleEdit = (category) => {
    setForm({
      name: category.name || '',
      color: category.color || '#4ECDC4',
      icon: category.icon || '',
      description: category.description || '',
      order: category.order || 0,
      group: category.group || '',
      parent_id: category.parent_id || null,
    })
    setEditingId(category.id)
    setError('')
    setEditModalOpen(true)
  }

  const handleDelete = (id) => {
    setConfirmMessage(t('Delete this category?', 'Delete this category?'))
    setConfirmAction(() => async () => {
      try {
        await apiClient.delete(`/categories/${id}`)
        fetchCategories()
      } catch (err) {
        setError(t('categories.failed_delete', 'Failed to delete category'))
      }
    })
    setConfirmOpen(true)
  }

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selected) => selected !== id) : [...prev, id]
    )
  }

  const handleBulkDelete = () => {
    const fallback = `Delete ${selectedIds.length} selected categories?`
    setConfirmMessage(
      t('categories.bulk_delete_confirm', fallback),
    )
    setConfirmAction(() => async () => {
      try {
        await apiClient.delete('/categories', { data: { ids: selectedIds } })
        setSelectedIds([])
        fetchCategories()
      } catch (err) {
        setError(t('categories.failed_bulk_delete', 'Failed to delete selected categories'))
      }
    })
    setConfirmOpen(true)
  }

  const handleBulkGroupChange = async () => {
    if (!bulkGroup.trim()) {
      setError(t('categories.bulk_group_required', 'Bulk group value is required'))
      return
    }
    try {
      await Promise.all(
        selectedIds.map((id) => apiClient.put(`/categories/${id}`, { group: bulkGroup }))
      )
      setSelectedIds([])
      setBulkGroup('')
      fetchCategories()
    } catch (err) {
      setError(t('categories.failed_bulk_group', 'Failed to update group for selected categories'))
    }
  }

  const handleBulkColorChange = async () => {
    if (!bulkColor) {
      setError(t('categories.bulk_color_required', 'Bulk color value is required'))
      return
    }
    try {
      await Promise.all(
        selectedIds.map((id) => apiClient.put(`/categories/${id}`, { color: bulkColor }))
      )
      setSelectedIds([])
      fetchCategories()
    } catch (err) {
      setError(t('Failed to update color for selected categories', 'Failed to update color for selected categories'))
    }
  }

  const confirmActionAccept = async () => {
    setConfirmOpen(false)
    if (typeof confirmAction === 'function') {
      await confirmAction()
    }
    setConfirmAction(null)
  }

  const confirmActionCancel = () => {
    setConfirmOpen(false)
    setConfirmAction(null)
  }

  const importDefaults = async () => {
    try {
      setError('')
      setLoading(true)
      await apiClient.post('/categories/import-defaults', {}, { params: { type: categoryType } })
      await fetchCategories()
    } catch (err) {
      setError(err.response?.data?.message || t('categories.failed_load', 'Failed to load categories'))
    } finally {
      setLoading(false)
    }
  }

  const handleRowOrderChange = (id, direction) => {
    setCategories((prev) => {
      const updated = [...prev]
      const index = updated.findIndex((cat) => cat.id === id)
      if (index === -1) return prev

      const target = index + direction
      if (target < 0 || target >= updated.length) return prev

      const [item] = updated.splice(index, 1)
      updated.splice(target, 0, item)

      // Recalculate order values after reorder
      return updated.map((cat, idx) => ({ ...cat, order: idx }))
    })
  }

  const saveOrder = async () => {
    try {
      const orderPayload = categories.map((cat) => ({ id: cat.id, order: cat.order }))
      await apiClient.post('/categories/reorder', { order: orderPayload })
      fetchCategories()
    } catch (err) {
      setError(t('categories.failed_save_order', 'Failed to save category order'))
    }
  }

  const filteredCategories = categories
    .filter((cat) => {
      const searchableName = String(cat.localized_name || cat.name || '').toLowerCase()
      return searchableName.includes(searchText.toLowerCase()) &&
        (groupFilter ? cat.group?.toLowerCase() === groupFilter.toLowerCase() : true)
    })

  const filteredCategoryIds = new Set(filteredCategories.map((cat) => cat.id))
  const categoriesById = new Map(categories.map((cat) => [cat.id, cat]))

  filteredCategories.forEach((cat) => {
    let parentId = cat.parent_id
    while (parentId) {
      filteredCategoryIds.add(parentId)
      parentId = categoriesById.get(parentId)?.parent_id
    }
  })

  const appliedCategories = categories
    .filter((cat) => filteredCategoryIds.has(cat.id))

  const visibleCategoryIds = appliedCategories.map((cat) => cat.id)
  const allSelected = visibleCategoryIds.length > 0 && visibleCategoryIds.every((id) => selectedIds.includes(id))
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : visibleCategoryIds)
  }

  const buildCategoryTree = (items) => {
    const itemMap = new Map(items.map((cat) => [cat.id, { ...cat, children: [] }]))
    const roots = []

    items.forEach((cat) => {
      const node = itemMap.get(cat.id)
      if (cat.parent_id && itemMap.has(cat.parent_id)) {
        itemMap.get(cat.parent_id).children.push(node)
      } else {
        roots.push(node)
      }
    })

    const sortNodes = (nodes) => {
      nodes.sort((a, b) => {
        if ((a.order ?? 0) === (b.order ?? 0)) {
          return a.name.localeCompare(b.name)
        }
        return (a.order ?? 0) - (b.order ?? 0)
      })
      nodes.forEach((node) => {
        if (node.children.length) {
          sortNodes(node.children)
        }
      })
    }

    sortNodes(roots)
    return roots
  }

  const categoryTree = buildCategoryTree(appliedCategories)

  const renderCategoryRows = (nodes, level = 0) =>
    nodes.flatMap((category) => [
      <tr key={category.id} className={selectedIds.includes(category.id) ? 'bg-blue-50' : ''}>
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="checkbox"
            checked={selectedIds.includes(category.id)}
            onChange={() => toggleSelect(category.id)}
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap" style={{ paddingLeft: `${level * 1.5}rem` }}>
          {category.localized_name || category.name}
        </td>
        {!isReceipt && (
          <>
            <td className="px-6 py-4 whitespace-nowrap">{category.order ?? 0}</td>
            <td className="px-6 py-4 whitespace-nowrap flex items-center gap-1">
              <button type="button" onClick={() => handleRowOrderChange(category.id, -1)} disabled={category.order === 0} className="text-muted-foreground hover:text-foreground">
                <ChevronUp size={16} />
              </button>
              <button type="button" onClick={() => handleRowOrderChange(category.id, 1)} className="text-muted-foreground hover:text-foreground">
                <ChevronDown size={16} />
              </button>
            </td>
          </>
        )}
        <td className="px-6 py-4 whitespace-nowrap">
          <span className="inline-block w-6 h-6 rounded-full" style={{ backgroundColor: category.color }}></span>
          <span className="ml-2 text-sm">{category.color}</span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">{formatEmptyValue(category.icon)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatEmptyValue(category.localized_description || category.description)}</td>
        <td className="px-6 py-4 whitespace-nowrap">{category.is_default ? t('categories.yes', 'Yes') : t('categories.no', 'No')}</td>
        <td className="px-6 py-4 whitespace-nowrap flex gap-2">
          <button
            type="button"
            onClick={() => handleEdit(category)}
            className={`${ui.buttonSmPrimary} flex items-center gap-1`}
          >
            <Edit3 size={16} /> {t('categories.edit', 'Edit')}
          </button>
          {(!category.is_default || isAdmin) && (
            <button
              type="button"
              onClick={() => handleDelete(category.id)}
              className={`${ui.buttonSmDanger} flex items-center gap-1`}
            >
              <Trash2 size={16} /> {t('categories.delete', 'Delete')}
            </button>
          )}
        </td>
      </tr>,
      ...(category.children.length ? renderCategoryRows(category.children, level + 1) : []),
    ])

  const allRows = renderCategoryRows(categoryTree)
  const pageCount = Math.max(1, Math.ceil(allRows.length / pageSize))
  const pageRows = allRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(pageCount)
    }
  }, [currentPage, pageCount])

  return (
    <PageShell>
      <div className={ui.card}>
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4 p-6">
          <div>
            <h1 className={ui.title}>{title}</h1>
            <p className={ui.subtitle}>{t('categories.subtitle', 'Manage your category list')}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!isReceipt && (
              <button
                type="button"
                onClick={saveOrder}
                className={ui.buttonPrimary}
              >
                {t('categories.save_order', 'Save Order')}
              </button>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-b border-outline flex flex-wrap items-center gap-3">
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className={ui.inputSm}
            placeholder={t('categories.search', 'Search categories')}
          />
          <input
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className={ui.inputSm}
            placeholder={t('categories.group_filter', 'Group filter')}
          />
          <button
            type="button"
            onClick={() => {
              setSearchText('')
              setGroupFilter('')
            }}
            className={ui.buttonSmGhost}
          >
            {t('categories.clear_filters', 'Clear Filters')}
          </button>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={openCreateModal}
              className={ui.buttonSuccess}
            >
              {t('categories.create', 'Create Category')}
            </button>
            {selectedIds.length > 0 && (
              <>
                <span className="text-sm text-muted-foreground">{selectedIds.length} {t('categories.selected', 'selected')}</span>
                <input
                  value={bulkGroup}
                  onChange={(e) => setBulkGroup(e.target.value)}
                  placeholder={t('categories.bulk_group', 'Bulk group')}
                  className={ui.inputSm}
                />
                <button
                  type="button"
                  onClick={handleBulkGroupChange}
                  className={ui.buttonSmPrimary}
                >
                  {t('categories.apply_group', 'Apply Group')}
                </button>
                <input
                  type="color"
                  value={bulkColor}
                  onChange={(e) => setBulkColor(e.target.value)}
                  className="h-10 w-10 p-0 border-0"
                />
                <button
                  type="button"
                  onClick={handleBulkColorChange}
                  className={ui.buttonSmSuccess}
                >
                  {t('categories.apply_color', 'Apply Color')}
                </button>
                <button
                  type="button"
                  onClick={handleBulkDelete}
                  className={ui.buttonSmDanger}
                >
                  {t('categories.delete', 'Delete')}
                </button>
              </>
            )}
          </div>
        </div>

        {editModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-3xl rounded-2xl bg-surface p-6 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className={ui.title}>{editingId ? t('categories.edit', 'Edit Category') : t('categories.create', 'Create Category')}</h2>
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {t('categories.close', 'Close')}
                </button>
              </div>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={ui.label}>{t('categories.name', 'Name')}</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={cn('mt-1', ui.input)}
                    placeholder={t('categories.name_placeholder', 'Category name')}
                  />
                </div>
                <div>
                  <label className={ui.label}>{t('categories.color', 'Color')}</label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                      className="h-10 w-10 p-0 border-0"
                    />
                    <span className="text-sm text-muted-foreground">{form.color}</span>
                  </div>
                </div>
                <div>
                  <label className={ui.label}>{t('categories.icon', 'Icon (emoji)')}</label>
                  <input
                    value={form.icon}
                    onChange={(e) => setForm({ ...form, icon: e.target.value })}
                    className={cn('mt-1', ui.input)}
                    placeholder={t('categories.icon_placeholder', 'e.g. рџЌЅпёЏ')}
                  />
                </div>
                <div>
                  <label className={ui.label}>{t('categories.group', 'Group')}</label>
                  <input
                    value={form.group}
                    onChange={(e) => setForm({ ...form, group: e.target.value })}
                    className={cn('mt-1', ui.input)}
                    placeholder={t('categories.group_placeholder', 'e.g. Personal, Living')}
                  />
                </div>
                <div>
                  <label className={ui.label}>{t('categories.parent', 'Parent Category')}</label>
                  <select
                    value={form.parent_id ?? ''}
                    onChange={(e) => setForm({ ...form, parent_id: e.target.value || null })}
                    className={cn('mt-1', ui.select)}
                  >
                    <option value="">No parent</option>
                    {categories
                      .filter((cat) => !editingId || cat.id !== editingId)
                      .map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.localized_name || cat.name}</option>
                      ))}
                  </select>
                </div>
                {!isReceipt && (
                  <div>
                    <label className={ui.label}>{t('categories.order', 'Order')}</label>
                    <input
                      type="number"
                      value={form.order}
                      min="0"
                      onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                      className={cn('mt-1', ui.input)}
                      placeholder="0"
                    />
                  </div>
                )}
                <div>
                  <label className={ui.label}>{t('categories.description', 'Description')}</label>
                  <input
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className={cn('mt-1', ui.input)}
                    placeholder={t('categories.description_placeholder', 'Optional description')}
                  />
                </div>
                <div className="md:col-span-2 flex items-center gap-2 mt-4">
                  <button type="submit" className={ui.buttonPrimary}>
                    {editingId ? <Save size={16} /> : <Plus size={16} />} {editingId ? t('categories.update', 'Update') : t('categories.create', 'Create')}
                  </button>
                  <button type="button" onClick={closeEditModal} className={ui.buttonGhost}>
                    <X size={16} /> {t('categories.cancel', 'Cancel')}
                  </button>
                </div>
              </form>
              {error && <p className={ui.alertError}>{error}</p>}
            </div>
          </div>
        )}

        <div className="bg-surface rounded-lg shadow overflow-hidden">
        
        {loading ? (
          <div className="p-6 flex justify-center">
            <div className={ui.spinner}></div>
          </div>
        ) : categories.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground">
            <p className="mb-4">{t('categories.no_categories', 'No categories found for this list.')}</p>
            {showImportDefaults && (
              <button
                type="button"
                onClick={importDefaults}
                className={ui.buttonPrimary}
              >
                {t('categories.import_defaults', 'Import default categories')}
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead className="bg-surface-elevated">
                  <tr>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 text-blue-600"
                      />
                    </th>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('categories.table.name', 'Name')}</th>
                    {!isReceipt && (
                      <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('categories.table.order', 'Order')}</th>
                    )}
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('categories.table.color', 'Color')}</th>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('categories.table.icon', 'Icon')}</th>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('categories.table.description', 'Description')}</th>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('categories.table.default', 'Default')}</th>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('categories.table.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-surface divide-y divide-divider">
                  {pageRows}
                </tbody>
              </table>
            </div>
            {pageCount > 1 && (
              <div className="px-6 py-4 bg-surface border-t border-outline flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{t('categories.page', 'Page')}</span>
                  <strong>{currentPage}</strong>
                  <span>{t('categories.of', 'of')}</span>
                  <strong>{pageCount}</strong>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={ui.buttonGhost}
                  >
                    {t('categories.prev', 'Prev')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, pageCount))}
                    disabled={currentPage === pageCount}
                    className={ui.buttonGhost}
                  >
                    {t('categories.next', 'Next')}
                  </button>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value))
                      setCurrentPage(1)
                    }}
                    className={ui.inputSm}
                  >
                    {[10, 20, 50, 100].map((size) => (
                      <option key={size} value={size}>{size} {t('categories.per_page', 'per page')}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title={t('Confirm Deletion', 'Confirm Deletion')}
        message={confirmMessage}
        onConfirm={confirmActionAccept}
        onCancel={confirmActionCancel}
        confirmText={t('Delete', 'Delete')}
        cancelText={t('Cancel', 'Cancel')}
      />
    </div>
  </PageShell>
  )
}

